import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LEGAL_PAGES } from "@/lib/legalContent";

function renderItem(item, keyBase) {
  if (typeof item === "object" && item.list) {
    return (
      <ul key={keyBase} className="my-3 list-disc space-y-1.5 pl-6 text-slate-600" data-testid="legal-list">
        {item.list.map((li) => (
          <li key={li} className="leading-relaxed">{li}</li>
        ))}
      </ul>
    );
  }
  return (
    <p key={keyBase} className="my-3 leading-relaxed text-slate-600">{item}</p>
  );
}

export default function LegalPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const page = LEGAL_PAGES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="legal-not-found">
        <h1 className="font-heading text-2xl font-bold">Pagină inexistentă</h1>
        <Button asChild className="mt-6 rounded-full"><Link to="/">Înapoi acasă</Link></Button>
      </div>
    );
  }

  return (
    <div data-testid={`legal-page-${slug}`}>
      <section className="bubble-bg noise-overlay">
        <div className="mx-auto max-w-4xl px-4 py-14 text-white sm:px-6 lg:px-8">
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-white/15 backdrop-blur">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="legal-title">
            {page.title}
          </h1>
          <p className="mt-3 text-sm text-white/80">Ultima actualizare: {page.updated}</p>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <Link to="/" className="mb-8 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary">
          <ArrowLeft className="h-4 w-4" /> Înapoi acasă
        </Link>

        {page.intro && (
          <p className="mb-8 rounded-xl border border-slate-200 bg-slate-50 p-5 leading-relaxed text-slate-700" data-testid="legal-intro">
            {page.intro}
          </p>
        )}

        <div className="space-y-8">
          {page.sections.map((section, idx) => (
            <section key={section.heading} data-testid={`legal-section-${idx}`}>
              <h2 className="font-heading text-lg font-bold text-slate-900">{section.heading}</h2>
              <div className="mt-2">{section.body.map((it, i) => renderItem(it, `${section.heading}-${i}`))}</div>
            </section>
          ))}
        </div>

        <div className="mt-12 border-t border-slate-200 pt-8">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-900">Documente legale</h3>
          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            <LegalLink slug="termeni-si-conditii" current={slug}>Termeni și Condiții</LegalLink>
            <LegalLink slug="confidentialitate" current={slug}>Confidențialitate (GDPR)</LegalLink>
            <LegalLink slug="politica-cookies" current={slug}>Politica de Cookie-uri</LegalLink>
            <LegalLink slug="politica-retur" current={slug}>Politica de Retur</LegalLink>
            <LegalLink slug="politica-livrare" current={slug}>Politica de Livrare</LegalLink>
            <LegalLink slug="protectia-consumatorului" current={slug}>Protecția Consumatorului</LegalLink>
          </div>
        </div>
      </article>
    </div>
  );
}

function LegalLink({ slug, current, children }) {
  const active = slug === current;
  return (
    <Link
      to={`/${slug}`}
      className={`rounded-full border px-4 py-1.5 transition-colors ${
        active
          ? "border-primary bg-primary text-white"
          : "border-slate-300 text-slate-600 hover:border-primary hover:text-primary"
      }`}
      data-testid={`legal-nav-${slug}`}
    >
      {children}
    </Link>
  );
}
